import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    ShieldExclamationIcon,
    ExclamationTriangleIcon,
    FolderOpenIcon,
    CheckBadgeIcon,
    ClockIcon,
    BoltIcon,
    ChartBarIcon,
    ArrowPathIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import {
    getFraudStats,
    runAllDetection,
    runSuspiciousPaymentsDetection,
    runHighCancellationDetection,
    runFakeBookingsDetection,
} from "../api/fraudSafetyApi";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import { useState } from "react";

// ============================================================================
// Stat Card
// ============================================================================

const StatCard = ({
    label,
    value,
    icon: Icon,
    color = "primary",
}: {
    label: string;
    value: number | string;
    icon: React.ElementType;
    color?: "primary" | "rose" | "amber" | "emerald" | "blue" | "orange" | "slate";
}) => {
    const colorMap: Record<string, { bg: string; icon: string; text: string }> = {
        primary: { bg: "bg-primary-50 dark:bg-primary-900/20", icon: "text-primary-600 dark:text-primary-400", text: "text-primary-700 dark:text-primary-300" },
        rose: { bg: "bg-rose-50 dark:bg-rose-900/20", icon: "text-rose-600 dark:text-rose-400", text: "text-rose-700 dark:text-rose-300" },
        amber: { bg: "bg-amber-50 dark:bg-amber-900/20", icon: "text-amber-600 dark:text-amber-400", text: "text-amber-700 dark:text-amber-300" },
        emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", icon: "text-emerald-600 dark:text-emerald-400", text: "text-emerald-700 dark:text-emerald-300" },
        blue: { bg: "bg-blue-50 dark:bg-blue-900/20", icon: "text-blue-600 dark:text-blue-400", text: "text-blue-700 dark:text-blue-300" },
        orange: { bg: "bg-orange-50 dark:bg-orange-900/20", icon: "text-orange-600 dark:text-orange-400", text: "text-orange-700 dark:text-orange-300" },
        slate: { bg: "bg-slate-50 dark:bg-slate-800/50", icon: "text-slate-500 dark:text-slate-400", text: "text-slate-600 dark:text-slate-400" },
    };
    const c = colorMap[color] || colorMap.primary;
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
                <div className={`rounded-xl p-2.5 ${c.bg}`}>
                    <Icon className={`h-5 w-5 ${c.icon}`} />
                </div>
                <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
                    <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Component
// ============================================================================

export const FraudOverviewRoute = () => {
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const canRunFraudJobs = hasPermission(SECTION_PERMISSIONS.FRAUD_RUN_JOBS[0]);
    const [detectionResult, setDetectionResult] = useState<string | null>(null);
    const [detectionError, setDetectionError] = useState<string | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ["fraud-stats"],
        queryFn: getFraudStats,
    });

    const stats = data?.data;

    // Detection mutations
    const runAllMutation = useMutation({
        mutationFn: runAllDetection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ["fraud-stats"] });
            queryClient.invalidateQueries({ queryKey: ["fraud-alerts"] });
            setDetectionResult(`Run All: ${res.data?.totalAlertsCreated ?? 0} alerts created`);
            setDetectionError(null);
        },
        onError: (err) => { setDetectionError(extractApiError(err)); setDetectionResult(null); },
    });

    const suspiciousPayMutation = useMutation({
        mutationFn: runSuspiciousPaymentsDetection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ["fraud-stats"] });
            queryClient.invalidateQueries({ queryKey: ["fraud-alerts"] });
            setDetectionResult(`Suspicious Payments: ${res.data?.alertsCreated ?? 0} alerts created`);
            setDetectionError(null);
        },
        onError: (err) => { setDetectionError(extractApiError(err)); setDetectionResult(null); },
    });

    const highCancelMutation = useMutation({
        mutationFn: runHighCancellationDetection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ["fraud-stats"] });
            queryClient.invalidateQueries({ queryKey: ["fraud-alerts"] });
            setDetectionResult(`High Cancellation: ${res.data?.alertsCreated ?? 0} alerts created`);
            setDetectionError(null);
        },
        onError: (err) => { setDetectionError(extractApiError(err)); setDetectionResult(null); },
    });

    const fakeBookingsMutation = useMutation({
        mutationFn: runFakeBookingsDetection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ["fraud-stats"] });
            queryClient.invalidateQueries({ queryKey: ["fraud-alerts"] });
            setDetectionResult(`Fake Bookings: ${res.data?.alertsCreated ?? 0} alerts created`);
            setDetectionError(null);
        },
        onError: (err) => { setDetectionError(extractApiError(err)); setDetectionResult(null); },
    });

    const anyPending =
        runAllMutation.isPending ||
        suspiciousPayMutation.isPending ||
        highCancelMutation.isPending ||
        fakeBookingsMutation.isPending;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load fraud stats</h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error).message}</p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
                <ChartBarIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No stats available</p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-8">
            {/* Alert Summary */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Alert Summary</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    <StatCard label="Total Alerts" value={stats.totalAlerts} icon={ShieldExclamationIcon} color="primary" />
                    <StatCard label="Last 24 Hours" value={stats.alertsLast24Hours} icon={ClockIcon} color="amber" />
                    <StatCard label="Last 7 Days" value={stats.alertsLast7Days} icon={ClockIcon} color="blue" />
                    <StatCard label="Last 30 Days" value={stats.alertsLast30Days} icon={ClockIcon} color="slate" />
                </div>
            </div>

            {/* Alert Status Breakdown */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Alerts by Status</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    <StatCard label="New" value={stats.newAlerts} icon={BoltIcon} color="blue" />
                    <StatCard label="Under Review" value={stats.underReviewAlerts} icon={ClockIcon} color="amber" />
                    <StatCard label="Resolved" value={stats.resolvedAlerts} icon={CheckBadgeIcon} color="emerald" />
                    <StatCard label="False Positive" value={stats.falsePositiveAlerts} icon={ShieldExclamationIcon} color="slate" />
                    <StatCard label="Escalated" value={stats.escalatedAlerts} icon={ExclamationTriangleIcon} color="rose" />
                </div>
            </div>

            {/* Alert Severity Breakdown */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Alerts by Severity</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard label="Critical" value={stats.criticalAlerts} icon={ExclamationTriangleIcon} color="rose" />
                    <StatCard label="High" value={stats.highAlerts} icon={ExclamationTriangleIcon} color="orange" />
                    <StatCard label="Medium" value={stats.mediumAlerts} icon={ShieldExclamationIcon} color="amber" />
                    <StatCard label="Low" value={stats.lowAlerts} icon={ShieldExclamationIcon} color="blue" />
                </div>
            </div>

            {/* Alerts by Type (last 30 days) */}
            {stats.alertsByTypeLast30Days && Object.keys(stats.alertsByTypeLast30Days).length > 0 && (
                <div>
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Alerts by Type (30 Days)</h2>
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                        <div className="space-y-3">
                            {Object.entries(stats.alertsByTypeLast30Days).map(([type, count]) => {
                                const maxCount = Math.max(...Object.values(stats.alertsByTypeLast30Days));
                                const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                return (
                                    <div key={type}>
                                        <div className="mb-1 flex items-center justify-between text-sm">
                                            <span className="text-slate-600 dark:text-slate-400">{type.replace(/_/g, " ")}</span>
                                            <span className="font-medium text-slate-900 dark:text-white">{count}</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                            <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Cases Summary */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Investigation Cases</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <StatCard label="Total Cases" value={stats.totalCases} icon={FolderOpenIcon} color="primary" />
                    <StatCard label="Open" value={stats.openCases} icon={FolderOpenIcon} color="blue" />
                    <StatCard label="In Progress" value={stats.inProgressCases} icon={ClockIcon} color="amber" />
                    <StatCard label="Escalated" value={stats.escalatedCases} icon={ExclamationTriangleIcon} color="rose" />
                    <StatCard label="Resolved" value={stats.resolvedCases} icon={CheckBadgeIcon} color="emerald" />
                    <StatCard label="Closed" value={stats.closedCases} icon={FolderOpenIcon} color="slate" />
                </div>
            </div>

            {/* Verification Summary */}
            <div>
                <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Verification Requests</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    <StatCard label="Total" value={stats.totalVerificationRequests} icon={CheckBadgeIcon} color="primary" />
                    <StatCard label="Pending" value={stats.pendingVerifications} icon={ClockIcon} color="amber" />
                    <StatCard label="Sent" value={stats.sentVerifications} icon={BoltIcon} color="blue" />
                    <StatCard label="Completed" value={stats.completedVerifications} icon={CheckBadgeIcon} color="emerald" />
                    <StatCard label="Expired" value={stats.expiredVerifications} icon={ExclamationTriangleIcon} color="rose" />
                </div>
            </div>

            {/* Detection Triggers */}
            {canRunFraudJobs ? (
                <div>
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Manual Detection</h2>
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
                            Manually trigger fraud detection algorithms. New alerts will be created for any suspicious activity found.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => runAllMutation.mutate()}
                                disabled={anyPending}
                            >
                                <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${runAllMutation.isPending ? "animate-spin" : ""}`} />
                                {runAllMutation.isPending ? "Running…" : "Run All Detection"}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => suspiciousPayMutation.mutate()}
                                disabled={anyPending}
                            >
                                {suspiciousPayMutation.isPending ? "Running…" : "Suspicious Payments"}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => highCancelMutation.mutate()}
                                disabled={anyPending}
                            >
                                {highCancelMutation.isPending ? "Running…" : "High Cancellation"}
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => fakeBookingsMutation.mutate()}
                                disabled={anyPending}
                            >
                                {fakeBookingsMutation.isPending ? "Running…" : "Fake Bookings"}
                            </Button>
                        </div>

                        {/* Detection result / error */}
                        {detectionResult && (
                            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                                {detectionResult}
                            </div>
                        )}
                        {detectionError && (
                            <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
                                {detectionError}
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
